import React, { useState, useEffect, useRef } from 'react';
import { usePhase } from '../../../contexts/PhaseContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  InputAdornment,
  Avatar,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Class as ClassIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { fetchJson } from '../../../utils/fetchJson';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';

const ManageClassesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const { activePhase } = usePhase();
  const resolvedPhaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '').toString().trim().toLowerCase();
  const isSecPhase = resolvedPhaseCode === 'sec';
  const levelOptions = isSecPhase
    ? [
        { value: 'first_cycle', label: t('classManagement.add.levels.firstCycle', { defaultValue: 'First Cycle' }) },
        { value: 'second_cycle', label: t('classManagement.add.levels.secondCycle', { defaultValue: 'Second Cycle' }) }
      ]
    : [
        { value: 'nursery', label: t('classManagement.add.levels.nursery', { defaultValue: 'Nursery' }) },
        { value: 'primary', label: t('classManagement.add.levels.primary', { defaultValue: 'Primary' }) }
      ];
  const classMasterLabel = isSecPhase
    ? t('classManagement.manage.labels.classMaster', { defaultValue: 'Class Master' })
    : t('classManagement.manage.labels.classTeacher', { defaultValue: 'Class Teacher' });
  const selectClassMasterLabel = isSecPhase
    ? t('classManagement.manage.dialog.selectClassMaster', { defaultValue: 'Select Class Master' })
    : t('classManagement.manage.dialog.selectClassTeacher', { defaultValue: 'Select Class Teacher' });
  const classMasterSuffixLabel = isSecPhase
    ? t('classManagement.manage.dialog.options.classMasterSuffix', { defaultValue: '(Class Master)' })
    : t('classManagement.manage.dialog.options.classTeacherSuffix', { defaultValue: '(Class Teacher)' });
  const getLevelLabel = (level) => {
    const normalizedLevel = (level || '').toString().trim().toLowerCase();
    const matched = levelOptions.find((option) => option.value === normalizedLevel);
    return matched?.label || level || t('classManagement.manage.na');
  };
  const manageTableColumnCount = isSecPhase ? 8 : 7;
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [compatibleTeachers, setCompatibleTeachers] = useState([]);
  const [compatibleLoading, setCompatibleLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Edit dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const reqSeqRef = useRef(0);
  const prevCtxRef = useRef('');
  const fetchData = async () => {
    const mySeq = ++reqSeqRef.current;
    try {
      setError(null);
      // Resolve context
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const ctxKey = `${schoolId}|${phaseCode || '-' }|${ayId || '-'}`;
      const clsCacheKey = `manageClasses:list:${ctxKey}`;

      // Clear on context change if no cache
      if (prevCtxRef.current !== ctxKey) {
        prevCtxRef.current = ctxKey;
        if (!localStorage.getItem(clsCacheKey)) setClasses([]);
      }
      // Pre-fill from cache
      try {
        const raw = localStorage.getItem(clsCacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (Array.isArray(cached) && reqSeqRef.current === mySeq) setClasses(cached);
        }
      } catch (_) {}

      // Helper: fast-fail timeout wrapper
      const withTimeout = (promise, ms = 3500) => Promise.race([
        promise,
        new Promise((_, rej) => setTimeout(() => rej(new Error('Timeout')), ms))
      ]);

      // Resolve numeric phaseId for phase-aware endpoints (e.g. enrollment-counts)
      let phaseIdStored = localStorage.getItem('schoolAdmin_activePhaseId')
        || localStorage.getItem('activePhaseId')
        || localStorage.getItem('phaseId');
      if ((!phaseIdStored || phaseIdStored === 'null' || phaseIdStored === 'undefined') && phaseCode) {
        try {
          const raw = localStorage.getItem('phaseCatalog');
          const arr = raw ? JSON.parse(raw) : [];
          if (Array.isArray(arr) && arr.length) {
            const found = arr.find(p => (p.code || '').toLowerCase() === String(phaseCode).toLowerCase());
            if (found && found.id) phaseIdStored = String(found.id);
          }
        } catch (_) {}
        // As a last resort, resolve via /api/phases
        if (!phaseIdStored && phaseCode) {
          try {
            const q = await fetchJson('/api/phases', { skipAuth: true });
            const arr = Array.isArray(q?.phases) ? q.phases : [];
            const found = arr.find(p => (p.code || '').toLowerCase() === String(phaseCode).toLowerCase());
            if (found && found.id) {
              phaseIdStored = String(found.id);
              try { localStorage.setItem('schoolAdmin_activePhaseId', phaseIdStored); } catch (_) {}
            }
          } catch (_) {}
        }
      }

      // Fetch classes and teachers in parallel to reduce total latency
      const headers = schoolId ? { 'X-Scope-School-Id': schoolId } : {};
      const q = `${phaseCode ? `?phase=${encodeURIComponent(phaseCode)}` : ''}${(phaseCode ? '&' : '?')}schoolId=${encodeURIComponent(schoolId)}`;
      const [p1, p2, p3, p4] = await Promise.allSettled([
        withTimeout(fetchJson(`/api/classes-lite${q}`, { headers }), 3000),
        // Use listUsers with a large pageSize to obtain full teacher/staff list (normalized later)
        withTimeout(fetchJson('/api/users?pageSize=500'), 3000),
        (schoolId && phaseCode && ayId && phaseIdStored)
          ? withTimeout(fetchJson(`/api/classes/enrollment-counts?schoolId=${encodeURIComponent(schoolId)}&phaseId=${encodeURIComponent(phaseIdStored)}&ayId=${encodeURIComponent(ayId)}`), 3000)
          : Promise.resolve({ counts: [] }),
        withTimeout(fetchJson('/api/global-academic-years'), 3000)
      ]);
      const classesData = p1.status === 'fulfilled' ? p1.value : { classes: [] };
      const teachersData = p2.status === 'fulfilled' ? p2.value : [];
      const countsData = p3 && p3.status === 'fulfilled' ? p3.value : { counts: [] };
      const yearsData = p4.status === 'fulfilled' ? p4.value : [];
      let classesArrayRaw = Array.isArray(classesData?.classes) ? classesData.classes : (Array.isArray(classesData) ? classesData : []);
      // Fallback: if empty *and no explicit phase*, try without phase filter (anyPhase=1) to avoid UI emptiness
      if ((!classesArrayRaw || classesArrayRaw.length === 0) && schoolId && !phaseCode) {
        try {
          const alt = await withTimeout(fetchJson(`/api/classes-lite?anyPhase=1&schoolId=${encodeURIComponent(schoolId)}`, { headers }), 2500);
          if (Array.isArray(alt?.classes) && alt.classes.length > 0) classesArrayRaw = alt.classes;
        } catch (_) {}
      }
      const counts = Array.isArray(countsData?.counts) ? countsData.counts : [];
      const yearsList = Array.isArray(yearsData?.years) ? yearsData.years : (Array.isArray(yearsData) ? yearsData : []);
      const ayLabel = (yearsList.find(y => String(y.id) === String(ayId))?.name) || (yearsList.find(y => String(y.id) === String(ayId))?.label) || '';
      const cMap = new Map(counts.map(c => [Number(c.classId), Number(c.count) || 0]));
      const classesArray = classesArrayRaw.map(c => ({
        ...c,
        studentCount: cMap.get(Number(c.id)) || 0,
        academicYear: ayLabel || '—',
      }));
      // Normalize teachers list from listUsers or legacy endpoints
      let teachersArrayRaw = Array.isArray(teachersData)
        ? teachersData
        : (teachersData?.rows || teachersData?.users || teachersData?.data || []);
      const teachersArray = (teachersArrayRaw || []).map(t => ({
        id: t.id ?? t.userId ?? t.staffId ?? t.teacherId ?? t.accountId,
        firstName: t.firstName ?? t.givenName ?? (t.name ? String(t.name).split(' ')[0] : ''),
        lastName: t.lastName ?? t.surname ?? (t.name ? String(t.name).split(' ').slice(1).join(' ') : ''),
        email: t.email || t.workEmail || '',
        avatarUrl: t.avatarUrl || null,
        name: t.name || ''
      })).filter(t => t.id != null);

      if (reqSeqRef.current === mySeq) {
        setClasses(classesArray);
        setTeachers(teachersArray);
        try { localStorage.setItem(clsCacheKey, JSON.stringify(classesArray)); } catch (_) {}
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError(t('classManagement.manage.errors.fetchData', {
        message: error?.userMessage || (error?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || '',
      }));
    } finally {
      if (reqSeqRef.current === mySeq) setLoading(false);
    }
  };

  // Auto-refresh AFTER fetchData is defined to avoid ReferenceError
  useScopeAutoRefresh([fetchData]);
  // Immediate reload on phase switch
  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activePhase]);
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('app:phase:changed', handler);
    window.addEventListener('app:ay:changed', handler);
    return () => {
      window.removeEventListener('app:phase:changed', handler);
      window.removeEventListener('app:ay:changed', handler);
    };
  }, []);

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setEditFormData({
      name: cls.name || '',
      level: cls.level || '',
      academicYear: cls.academicYear || '',
      classMasterId: cls.classMasterId || '',
      isActive: cls.isActive !== false,
      hasSubjectSelection: !!(cls.hasSubjectSelection || cls.isExaminationClass),
      isExaminationClass: !!cls.isExaminationClass
    });
    setEditDialogOpen(true);

    // Load server-assisted compatible teachers for this class (scoped by school/phase/AY)
    (async () => {
      try {
        setCompatibleLoading(true);
        setCompatibleTeachers([]);
        const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
        const phaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '');
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const headers = schoolId
          ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId }
          : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };
        const params = [];
        if (schoolId) params.push(`schoolId=${encodeURIComponent(schoolId)}`);
        if (phaseId) params.push(`phaseId=${encodeURIComponent(phaseId)}`);
        else if (phaseCode) params.push(`phase=${encodeURIComponent(phaseCode)}`);
        if (ayId) params.push(`ayId=${encodeURIComponent(ayId)}`);
        const q = params.length ? `?${params.join('&')}` : '';
        const resp = await fetchJson(`/api/classes/${cls.id}/compatible-teachers${q}`, { headers }).catch(() => ({ teachers: [] }));
        const arr = Array.isArray(resp) ? resp : (resp?.teachers || resp?.data || []);
        const norm = (arr || []).map(t => ({
          id: t.id ?? t.userId ?? t.teacherId ?? t.accountId,
          firstName: t.firstName ?? t.givenName ?? (t.name?.split(' ')[0]) ?? '',
          lastName: t.lastName ?? t.surname ?? (t.name?.split(' ').slice(1).join(' ')) ?? '',
          email: t.email || '',
          name: t.name || ''
        })).filter(t => t.id != null);
        setCompatibleTeachers(norm);
      } finally {
        setCompatibleLoading(false);
      }
    })();
  };

  const getCompatibleTeachers = (classId) => {
    if (!classId) return [];
    if (editingClass && String(editingClass.id) === String(classId) && Array.isArray(compatibleTeachers) && compatibleTeachers.length > 0) {
      return compatibleTeachers;
    }
    return [];
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);

      const payload = {
        ...editFormData,
        level: editFormData.level || null,
        hasSubjectSelection: isSecPhase ? !!(editFormData.hasSubjectSelection ?? editFormData.isExaminationClass) : false,
        isExaminationClass: isSecPhase ? !!editFormData.isExaminationClass : false,
      };
      
      await fetchJson(`/api/classes/${editingClass.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      enqueueSnackbar(t('classManagement.manage.toasts.updateSuccess'), { variant: 'success' });
      
      // Close dialog and refresh data
      setEditDialogOpen(false);
      setEditingClass(null);
      setEditFormData({});
      // Immediate refresh (no artificial delay)
      fetchData();
      
    } catch (error) {
      console.error('Error updating class:', error);
      enqueueSnackbar(t('classManagement.manage.toasts.updateError', {
        message: error?.userMessage || (error?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || '',
      }), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (classId) => {
    if (window.confirm(t('classManagement.manage.confirmDelete'))) {
      try {
        // Optimistic UI update for speed
        setClasses(prev => prev.filter(c => c.id !== classId));
        // Fire and forget
        fetchJson(`/api/classes/${classId}`, { method: 'DELETE' })
          .then(() => enqueueSnackbar(t('classManagement.manage.toasts.deleteSuccess'), { variant: 'success' }))
          .catch((error) => enqueueSnackbar(t('classManagement.manage.toasts.deleteError', {
            message: error?.userMessage || (error?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || '',
          }), { variant: 'error' }));
      
      } catch (error) {
        console.error('Error deleting class:', error);
        enqueueSnackbar(t('classManagement.manage.toasts.deleteError', {
          message: error?.userMessage || (error?.message || '').replace(/^HTTP\s+\d+[:\s]*/i, '').trim() || '',
        }), { variant: 'error' });
      }
    }
  };

  const getTeacherName = (teacherId) => {
    if (!teacherId) {
      return t('classManagement.manage.notAssigned');
    }
    const teacher = Array.isArray(teachers)
      ? teachers.find(t => String(t.id) === String(teacherId))
      : null;
    if (!teacher) {
      return t('classManagement.manage.notAssigned');
    }
    const first = teacher.firstName || (teacher.name ? String(teacher.name).split(' ')[0] : '');
    const last = teacher.lastName || (teacher.name ? String(teacher.name).split(' ').slice(1).join(' ') : '');
    const full = `${first || ''} ${last || ''}`.trim() || teacher.name || teacher.email;
    return full || t('classManagement.manage.notAssigned');
  };

  const filteredClasses = Array.isArray(classes) ? classes.filter(cls => {
    const matchesSearch = cls.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         false;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && cls.isActive !== false) ||
                         (statusFilter === 'inactive' && cls.isActive === false);
    
    return matchesSearch && matchesStatus;
  }) : [];

  // Render immediately; show optional inline loader instead of blocking the whole page

  // Safety check to ensure data is properly loaded
  if (!Array.isArray(classes) || !Array.isArray(teachers)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('classManagement.manage.errors.dataNotLoaded')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/class-management')}
          sx={{ mr: 2 }}
        >
          {t('classManagement.manage.back')}
        </Button>
        <Box>
          <Typography variant="h4" component="h1">
            {t('classManagement.manage.pageTitle')}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t('classManagement.manage.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder={t('classManagement.manage.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('classManagement.manage.status.label')}</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label={t('classManagement.manage.status.label')}
              >
                <MenuItem value="all">{t('classManagement.manage.status.all')}</MenuItem>
                <MenuItem value="active">{t('classManagement.manage.status.active')}</MenuItem>
                <MenuItem value="inactive">{t('classManagement.manage.status.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/class-management/add-class')}
              >
                {t('classManagement.manage.actions.addClass')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Classes Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
          <Table sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('classManagement.manage.dialog.fields.name', { defaultValue: 'Class Name' })}</TableCell>
                <TableCell>{t('classManagement.add.single.fields.level', { defaultValue: 'Level' })}</TableCell>
                <TableCell>{classMasterLabel}</TableCell>
                <TableCell>{t('classManagement.manage.dialog.fields.status', { defaultValue: 'Status' })}</TableCell>
                {isSecPhase && (
                  <TableCell>{t('classManagement.manage.dialog.fields.subjectSelectionRequired', { defaultValue: 'Subject Selection Required' })}</TableCell>
                )}
                <TableCell>{t('classManagement.manage.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClasses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={manageTableColumnCount} align="center">
                    <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                      {t('classManagement.manage.table.empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredClasses.map((cls) => (
                  <TableRow key={cls.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ClassIcon sx={{ mr: 1, color: 'primary.main' }} />
                        {cls.name}
                      </Box>
                    </TableCell>
                    <TableCell>{getLevelLabel(cls.level)}</TableCell>
                    <TableCell>
                      {cls.classMasterId ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {(() => {
                            const teacher = teachers.find(t => String(t.id) === String(cls.classMasterId));
                            const displayName = getTeacherName(cls.classMasterId);
                            if (!teacher) {
                              return (
                                <Chip
                                  label={displayName}
                                  color="primary"
                                  size="small"
                                  sx={{ maxWidth: 200 }}
                                />
                              );
                            }
                            return (
                              <>
                                <Avatar
                                  sx={{ width: 28, height: 28, mr: 1, bgcolor: 'primary.main', fontSize: 14 }}
                                  src={teacher.avatarUrl || undefined}
                                >
                                  {(displayName || '?').charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {displayName}
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      ) : (
                        <Chip
                          label={t('classManagement.manage.notAssigned')}
                          color="default"
                          size="small"
                          sx={{ maxWidth: 200 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cls.isActive !== false
                          ? t('classManagement.manage.status.chipActive')
                          : t('classManagement.manage.status.chipInactive')}
                        color={cls.isActive !== false ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    {isSecPhase && (
                      <TableCell>
                        <Chip
                          label={cls.hasSubjectSelection || cls.isExaminationClass
                            ? t('common.yes', { defaultValue: 'Yes' })
                            : t('common.no', { defaultValue: 'No' })}
                          color={cls.hasSubjectSelection || cls.isExaminationClass ? 'info' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={t('classManagement.manage.actions.view')}>
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('classManagement.manage.actions.edit')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(cls)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('classManagement.manage.actions.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(cls.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('classManagement.manage.dialog.title')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('classManagement.manage.dialog.fields.name')}
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('classManagement.add.single.fields.level')}</InputLabel>
                <Select
                  value={editFormData.level || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, level: e.target.value })}
                  label={t('classManagement.add.single.fields.level')}
                >
                  {levelOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {/* Read-only: the academic year comes from the switcher in the
                  top bar, which is what every scoped read and write already
                  uses. An editable box here implied a class could be moved to
                  another year by typing over it — it cannot, and updateClass
                  does not accept the field at all, so anything typed was
                  discarded on save. */}
              <TextField
                fullWidth
                disabled
                label={t('classManagement.manage.dialog.fields.academicYear')}
                value={editFormData.academicYear || ''}
                helperText={t('classManagement.manage.dialog.academicYearHint', {
                  defaultValue: 'Set by the academic year switcher',
                })}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{classMasterLabel}</InputLabel>
                <Select
                  value={editFormData.classMasterId}
                  onChange={(e) => setEditFormData({ ...editFormData, classMasterId: e.target.value })}
                  label={classMasterLabel}
                >
                  <MenuItem value="">
                    <em>{selectClassMasterLabel}</em>
                  </MenuItem>
                  {compatibleLoading && (
                    <MenuItem disabled value="__loading">
                      <em>{t('classManagement.manage.dialog.options.loadingTeachers')}</em>
                    </MenuItem>
                  )}
                  {!compatibleLoading && getCompatibleTeachers(editingClass?.id).length === 0 && (
                    <MenuItem disabled value="__none">
                      <em>{t('classManagement.manage.dialog.options.noCompatibleTeachers')}</em>
                    </MenuItem>
                  )}
                  {!compatibleLoading && getCompatibleTeachers(editingClass?.id).map((teacher) => {
                    const first = teacher.firstName || (teacher.name ? String(teacher.name).split(' ')[0] : '');
                    const last = teacher.lastName || (teacher.name ? String(teacher.name).split(' ').slice(1).join(' ') : '');
                    const baseLabel = (`${first || ''} ${last || ''}`.trim()) || teacher.name || teacher.email || String(teacher.id);
                    const isAlreadyClassMaster = classes.some(c => String(c.classMasterId) === String(teacher.id));
                    const isOtherClassMaster = isAlreadyClassMaster && editingClass && String(editingClass.classMasterId) !== String(teacher.id);
                    const label = isOtherClassMaster ? `${baseLabel} ${classMasterSuffixLabel}` : baseLabel;
                    return (
                      <MenuItem key={teacher.id} value={teacher.id} disabled={isOtherClassMaster}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('classManagement.manage.dialog.fields.status')}</InputLabel>
                <Select
                  value={editFormData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.value === 'active' })}
                  label={t('classManagement.manage.dialog.fields.status')}
                >
                  <MenuItem value="active">{t('classManagement.manage.status.chipActive')}</MenuItem>
                  <MenuItem value="inactive">{t('classManagement.manage.status.chipInactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              {isSecPhase && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!(editFormData.hasSubjectSelection ?? editFormData.isExaminationClass)}
                      onChange={(e) => setEditFormData({ ...editFormData, hasSubjectSelection: e.target.checked, isExaminationClass: e.target.checked })}
                    />
                  }
                  label={t('classManagement.manage.dialog.fields.subjectSelectionRequired')}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('classManagement.manage.dialog.cancel')}</Button>
          <Button onClick={handleUpdate} variant="contained">{t('classManagement.manage.dialog.update')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageClassesPage;
